import { useEffect, useRef, useState } from 'react'
import { SectionCard } from './DetailBits'
import { formatRate } from './bookingDetailsData'
import { useSuggestProducts, suggestionPrice } from './useSuggestProducts'

/**
 * Astrologer-side "Suggest Products" — search the gemstone catalog and pin
 * remedies to this consultation (full-width panel on the appointment page;
 * the video console renders the same engine through SuggestProductCard).
 * All data flows through useSuggestProducts, which owns the verified
 * upstream contracts.
 *
 * Runs the engine in deferred mode: a suggestion is a row in the customer's
 * cart, so publishing each pick as it is made would drop products on them
 * mid-consultation. Picks are staged (here and in the video room, through the
 * same per-booking store) and published in one batch when the astrologer
 * finalizes the note — the page drives that through `commitRef`.
 */

const fieldCls =
  'w-full rounded-xl border border-[#C8922A]/20 bg-[#FDFBF7] px-3.5 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:border-[#C8922A] disabled:opacity-50'
const fieldLabelCls = 'block text-[9px] font-bold uppercase tracking-[0.18em] text-[#8B7355] mb-1.5'

export default function SuggestProductPanel({ booking, disabled, lockedReason, commitRef }) {
  const [showFilters, setShowFilters] = useState(false)
  const {
    filters, setField, clearFilters, activeFilterCount, submitSearch,
    searchState, results, fetching, searchError, page, setPage,
    categories, grades,
    suggestions, suggestionsLoading, suggestedProductIds,
    suggest, addingId, removeSuggestion, removingId, error,
    commit, committing, pendingCount,
  } = useSuggestProducts(booking, { perPage: 8, deferred: true })

  // Hand the page a live handle on commit. No dep array on purpose: `commit`
  // closes over the staged rows, so the ref has to be refreshed every render
  // or "Confirm Finalize" would flush a stale batch.
  useEffect(() => {
    if (!commitRef) return undefined
    commitRef.current = commit
    return () => {
      commitRef.current = null
    }
  })

  return (
    <SectionCard title="Suggested Remedies" icon="💎">
      <p className="text-xs text-[#8B7355] -mt-2 mb-5">
        Recommend gemstones and remedies from the shop — they reach the customer&rsquo;s cart when you finalize this
        consultation, not before.
      </p>

      {/* Already-suggested list — always visible, even once locked. */}
      <SuggestedList
        suggestions={suggestions}
        loading={suggestionsLoading}
        disabled={disabled}
        removingId={removingId}
        onRemove={removeSuggestion}
      />

      {/* The customer sees none of this until the note is finalized — say so,
          or the astrologer has no way to tell staged picks from published. */}
      {pendingCount > 0 && (
        <div className="mt-4 rounded-xl border border-[#C8922A]/30 bg-[#C8922A]/8 px-4 py-2.5 text-xs text-[#8B7355]">
          {committing
            ? '⏳ Sharing with the customer…'
            : `${pendingCount} change${pendingCount === 1 ? '' : 's'} pending — shared with the customer when you finalize this consultation below.`}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 px-4 py-2.5 text-xs text-red-600">
          {error}
        </div>
      )}

      {disabled ? (
        <div className="mt-5 rounded-xl border border-[#C8922A]/20 bg-[#FDFBF7] px-4 py-3 text-xs text-[#8B7355]">
          🔒 {lockedReason || 'Product suggestions are locked for this appointment.'}
        </div>
      ) : (
        <div className="mt-6 pt-6 border-t border-dashed border-[#C8922A]/20">
          {/* Search bar */}
          <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative grow">
              <span aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C8922A]/70 text-sm">🔍</span>
              <input
                type="text"
                value={filters.q}
                onChange={setField('q')}
                placeholder="Search the catalog — e.g. Yellow Sapphire, Rudraksha…"
                className={`${fieldCls} pl-10 py-3`}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'border-[#C8922A]/50 bg-[#C8922A]/10 text-[#C8922A]'
                    : 'border-[#C8922A]/25 text-[#8B7355] hover:bg-[#C8922A]/5'
                }`}
              >
                ⚙ Refine
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-4.5 h-4.5 px-1 rounded-full bg-[#C8922A] text-white text-[9px]">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                type="submit"
                disabled={fetching}
                className="hero-btn-primary inline-flex items-center gap-2 rounded-xl px-6 py-3 text-[10px] font-bold uppercase tracking-[0.18em] disabled:opacity-60"
              >
                {fetching ? 'Searching…' : 'Search'}
              </button>
            </div>
          </form>

          {/* Advanced filters */}
          {showFilters && (
            <div className="mt-4 rounded-2xl border border-[#C8922A]/15 bg-[#FDFBF7]/60 p-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className={fieldLabelCls}>Category</label>
                  <select value={filters.category_id} onChange={setField('category_id')} className={fieldCls}>
                    <option value="">All categories</option>
                    <CategoryOptions categories={categories} />
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={fieldLabelCls}>Quality Grade</label>
                  <select value={filters.product_grade_id} onChange={setField('product_grade_id')} className={fieldCls}>
                    <option value="">Any grade</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={fieldLabelCls}>Ratti</label>
                  <input type="number" min="0" step="0.01" value={filters.ratti} onChange={setField('ratti')} placeholder="e.g. 7.5" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Carat</label>
                  <input type="number" min="0" step="0.01" value={filters.carat} onChange={setField('carat')} placeholder="e.g. 6.75" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Min Price (₹)</label>
                  <input type="number" min="0" value={filters.min_price} onChange={setField('min_price')} placeholder="500" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Max Price (₹)</label>
                  <input type="number" min="0" value={filters.max_price} onChange={setField('max_price')} placeholder="50000" className={fieldCls} />
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-[#C8922A]/10">
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  <span className="relative inline-block w-9 h-5">
                    <input type="checkbox" checked={filters.in_stock} onChange={setField('in_stock')} className="peer sr-only" />
                    <span className="absolute inset-0 rounded-full bg-stone-300 peer-checked:bg-[#C8922A] transition-colors" />
                    <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </span>
                  <span className="text-xs font-semibold text-[#1C140A]">In-stock only</span>
                </label>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] hover:text-[#C8922A] transition-colors"
                >
                  ✕ Clear filters
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <SearchResults
            state={searchState}
            results={results}
            fetching={fetching}
            page={page}
            onPage={setPage}
            suggestedProductIds={suggestedProductIds}
            addingId={addingId}
            onSuggest={suggest}
            errorMessage={searchError}
          />
        </div>
      )}
    </SectionCard>
  )
}

/** Grouped <option>s: parents as optgroups when they have children. The
 * upstream /categories tree nests children; flat rows fall back to a plain
 * list keyed off parent_id. */
export function CategoryOptions({ categories }) {
  const roots = categories.filter((c) => !c.parent_id)
  const childrenOf = (parent) =>
    Array.isArray(parent.children) && parent.children.length
      ? parent.children
      : categories.filter((c) => c.parent_id === parent.id)

  return roots.map((parent) => {
    const children = childrenOf(parent)
    if (!children.length) {
      return <option key={parent.id} value={parent.id}>{parent.name}</option>
    }
    return (
      <optgroup key={parent.id} label={parent.name}>
        <option value={parent.id}>All {parent.name}</option>
        {children.map((child) => (
          <option key={child.id} value={child.id}>{child.name}</option>
        ))}
      </optgroup>
    )
  })
}

/**
 * Suggested products as a horizontal strip of box-shaped tiles — the same
 * design as the video console's card, in the page's light theme. One tile
 * tall; overflow scrolls sideways (wheel/touch plus ◀▶ buttons).
 */
function SuggestedList({ suggestions, loading, disabled, removingId, onRemove }) {
  const scrollRef = useRef(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const syncScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setCanLeft(el.scrollLeft > 2)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  // Re-measure when tiles are added/removed (a removal can kill the overflow).
  useEffect(() => {
    syncScroll()
  }, [suggestions.length])

  const nudge = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' })
  }

  const overflowing = canLeft || canRight

  if (loading) {
    return (
      <div className="flex gap-3 animate-pulse">
        {[0, 1, 2].map((i) => (
          <div key={i} className="w-32 h-44 rounded-xl bg-[#FDFBF7] border border-[#C8922A]/10" />
        ))}
      </div>
    )
  }

  if (!suggestions.length) {
    return (
      <div className="rounded-xl border border-dashed border-[#C8922A]/25 bg-[#FDFBF7]/50 px-5 py-6 text-center">
        <p className="text-2xl mb-1.5 opacity-60">🪔</p>
        <p className="text-sm font-semibold text-[#1C140A]">No remedies suggested yet</p>
        <p className="text-xs text-[#8B7355] mt-1">Search the catalog below and suggest products for this customer.</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-[#C8922A]/20 bg-[#FDFBF7]/60">
      <div className="flex items-center justify-between pl-4 pr-3 pt-2.5 pb-1.5">
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#C8922A]">
          Suggested ({suggestions.length})
        </span>
        {overflowing && (
          <div className="flex items-center gap-1.5">
            <ScrollBtn label="◀" title="Scroll left" disabled={!canLeft} onClick={() => nudge(-1)} />
            <ScrollBtn label="▶" title="Scroll right" disabled={!canRight} onClick={() => nudge(1)} />
          </div>
        )}
      </div>
      <ul
        ref={scrollRef}
        onScroll={syncScroll}
        className="flex gap-3 overflow-x-auto px-3 pb-3 scrollbar-thin [scrollbar-color:rgba(200,146,42,0.35)_transparent]"
      >
        {suggestions.map((row) => {
          const product = row.product ?? {}
          // Suggestion rows carry the image under `image` (not image_url).
          const image = product.image || product.image_url || null
          const price = suggestionPrice(row)
          const qty = Number(row.quantity) || 1
          const removing = removingId === row.id
          return (
            <li
              key={row.id}
              className="relative shrink-0 w-32 rounded-xl border border-[#C8922A]/15 bg-white overflow-hidden shadow-sm"
            >
              <Thumb src={image} alt={product.name} variant="tile" />
              {row.staged && (
                <span className="absolute top-1.5 left-1.5 rounded-full bg-[#C8922A] px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white">
                  Pending
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  disabled={removing}
                  onClick={() => onRemove(row.id)}
                  title="Remove this suggestion"
                  className="absolute top-1.5 right-1.5 w-6 h-6 inline-flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm border border-red-200 text-red-500 hover:bg-red-50 transition-colors text-[10px] disabled:opacity-40"
                >
                  {removing ? '…' : '✕'}
                </button>
              )}
              <div className="px-2.5 py-2">
                <p className="text-[11px] font-semibold text-[#1C140A] leading-tight line-clamp-2 min-h-7">
                  {product.name || `Product #${row.product_id}`}
                </p>
                <p className="text-[10px] text-[#8B7355] mt-1">
                  Qty {qty}
                  {price != null && <> · <span className="font-semibold text-[#1C140A]">{formatRate(price)}</span></>}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ScrollBtn({ label, title, disabled, onClick }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="w-6.5 h-6.5 inline-flex items-center justify-center rounded-full border border-[#C8922A]/30 text-[#C8922A] text-[9px] hover:bg-[#C8922A]/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}

function SearchResults({ state, results, fetching, page, onPage, suggestedProductIds, addingId, onSuggest, errorMessage }) {
  if (state === 'idle') return null

  if (state === 'loading') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 animate-pulse">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-2xl bg-[#FDFBF7] border border-[#C8922A]/10" />
        ))}
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="mt-6 rounded-xl border border-red-200 bg-red-50/60 px-5 py-4 text-sm text-red-600">
        {errorMessage}
      </div>
    )
  }

  if (!results.items.length) {
    return (
      <div className="mt-6 rounded-2xl border border-[#C8922A]/15 bg-[#FDFBF7]/50 px-6 py-10 text-center">
        <p className="text-3xl mb-2 opacity-50">✨</p>
        <p className="font-cinzel text-base text-[#1C140A]">No products matched</p>
        <p className="text-xs text-[#8B7355] mt-1.5">Try a broader search term or relax the filters.</p>
      </div>
    )
  }

  return (
    <div className={`mt-6 ${fetching ? 'opacity-60 pointer-events-none' : ''} transition-opacity`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8B7355] mb-3">
        {results.total} product{results.total === 1 ? '' : 's'} found
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {results.items.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            suggested={suggestedProductIds.has(Number(product.id))}
            adding={addingId === product.id}
            onSuggest={onSuggest}
          />
        ))}
      </div>
      {results.lastPage > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <PagerBtn label="← Prev" disabled={page <= 1} onClick={() => onPage(page - 1)} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355]">
            Page {results.currentPage} of {results.lastPage}
          </span>
          <PagerBtn label="Next →" disabled={page >= results.lastPage} onClick={() => onPage(page + 1)} />
        </div>
      )}
    </div>
  )
}

function PagerBtn({ label, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-full border border-[#C8922A]/30 px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#C8922A] hover:bg-[#C8922A]/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}

function ProductCard({ product, suggested, adding, onSuggest }) {
  const [qty, setQty] = useState(1)
  const price = Number(product.price)
  const outOfStock = product.in_stock === false

  return (
    <div className="group rounded-2xl border border-[#C8922A]/15 bg-white hover:border-[#C8922A]/45 hover:shadow-md transition-all overflow-hidden flex">
      <Thumb src={product.image_url} alt={product.name} className="w-24 sm:w-27 self-stretch" variant="edge" />
      <div className="min-w-0 grow p-3.5 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-[#1C140A] leading-snug line-clamp-2">{product.name}</p>
          {outOfStock ? (
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-widest text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Out</span>
          ) : (
            <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5" title="In stock" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {product.grade && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-[#C8922A] bg-[#C8922A]/10 rounded-full px-2 py-0.5">{product.grade}</span>
          )}
          {product.category && (
            <span className="text-[9px] font-semibold uppercase tracking-wider text-[#8B7355] bg-stone-100 rounded-full px-2 py-0.5">{product.category}</span>
          )}
          {product.ratti && <span className="text-[10px] text-[#8B7355]">{product.ratti} ratti</span>}
        </div>
        <div className="flex items-end justify-between gap-3 mt-auto pt-3">
          <p className="font-cinzel text-lg text-[#1C140A] leading-none">
            {Number.isFinite(price) ? formatRate(price) : '—'}
          </p>
          {suggested ? (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5">
              ✓ Suggested
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center rounded-full border border-[#C8922A]/25 overflow-hidden">
                <QtyBtn label="−" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} />
                <span className="w-6 text-center text-xs font-semibold text-[#1C140A]">{qty}</span>
                <QtyBtn label="+" onClick={() => setQty((q) => Math.min(99, q + 1))} />
              </div>
              <button
                type="button"
                disabled={adding || outOfStock}
                onClick={() => onSuggest(product, qty)}
                title={outOfStock ? 'This product is out of stock.' : 'Suggest to the customer'}
                className="hero-btn-primary rounded-full px-4 py-1.5 text-[9px] font-bold uppercase tracking-widest disabled:opacity-40"
              >
                {adding ? '…' : '+ Suggest'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function QtyBtn({ label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-6.5 h-6.5 inline-flex items-center justify-center text-sm text-[#C8922A] hover:bg-[#C8922A]/10 transition-colors disabled:opacity-30"
    >
      {label}
    </button>
  )
}

/** Product image with a jewel placeholder when the catalog has none.
 * thumb: standalone framed square · edge: flush card-left panel ·
 * tile: full-width square atop a suggestion tile. */
function Thumb({ src, alt, className = '', variant = 'thumb' }) {
  // Degrade to the placeholder on load failure too — the admin server
  // currently 403s /storage/*, so catalog image URLs can be dead.
  const [failed, setFailed] = useState(false)
  const frame = {
    thumb: 'aspect-square rounded-lg border border-[#C8922A]/15',
    edge: 'border-r border-[#C8922A]/10',
    tile: 'aspect-square w-full border-b border-[#C8922A]/10',
  }[variant]
  return (
    <div className={`bg-[#FDFBF7] flex items-center justify-center overflow-hidden shrink-0 ${frame} ${className}`}>
      {src && !failed ? (
        <img
          src={src}
          alt={alt || 'Product'}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-xl opacity-40" aria-hidden="true">💎</span>
      )}
    </div>
  )
}
